<template>
    <div class="player-detail" v-if="items.length"
        :style="{ 'background-color': color }"
    >
        <div class="buttons">
 
        </div>

        <div class="slider-box">
            <Slider
                :colPreloadCount="3"
                :items="items"
                :itemsInCol="1"
                :loop="false"
                :autoScroll="true"
                :storyItem="true"
                itemWidth="80%"
                colAlign="center"
                colGap="8px"
                rowGap="8px"
                ref="sliderRef"
                @change="onChange"
            >
                <template #default="{ item }">
                    <div class="slide"
                        :class="{ active: item.id === id }"
                    >
                        <Img v-if="item.cover?.crop || defaultCover?.crop || props.skeleton"
                            :preview="item.cover?.crop || defaultCover?.crop"
                            :original="item.cover?.original || defaultCover?.original"
                            :skeleton="true"
                            :checktone="true"
                            @color="data.colors[item.id] = $event"
                            @tone="data.tones[item.id] = $event"
                            aspectRatio="1 / 1"
                        />
                    </div>
                </template>
            </Slider>
        </div>


        <div class="main">
            <div class="preview-info">
                <TrackPreview
                    :cover="false"
                    :item="item"
                    :playable="false"
                    :light="true"
                    :showKebab="true"
                    :checkcolor="true"
                    :viewonly="true"
                    :invert="!sameColors"
                    size="size-l"
                    @color="data.colors[item.id] = $event"
                />
            </div>

            <div v-if="item?.wave" class="track-wave-box">
                <TrackWave 
                    :wave="item.wave"
                    :id="item.id"
                    :duration="item.duration"
                    :simplify="2"
                    :class="{ invert: sameColors }"
                    class="wave"
                />
            </div>


            <div class="controls" v-if="item?.id">
                <Reaction
                    entity="tracks"
                    :id="id"
                    size="size-l"
                    :tone="data.tones[id]"
                    :touchMode="true"
                    type="DISLIKE"
                />

                <IconButton :icon="locale.rtl ? 'player-forward' : 'player-back'"
                    class="size-l tertiary"
                    :disabled="player.panes.default?.prevDisabled"
                    :class="{ invert: !sameColors }"
                    @touchend.prevent.stop="prev"
                />

                <AudioToggle
                    :state="id === item.id ? player.panes.default?._state : null"
                    :light="true"
                    :touchMode="true"
                    class="size-l secondary dirty"
                    :class="{ invert: others.theme === 'dark' ? !sameColors : sameColors }"
                    @play="play"
                    @pause="pause"
                />

                <IconButton :icon="locale.rtl ? 'player-back' : 'player-forward'"
                    class="size-l tertiary"
                    :disabled="player.panes.default?.nextDisabled"
                    :class="{ invert: !sameColors }"
                    @touchend.prevent.stop="next"
                />

                <Reaction
                    entity="tracks"
                    :id="id"
                    size="size-l"
                    :tone="data.tones[id]"
                    :touchMode="true"
                />
            </div>
        </div>


        <div class="buttons">
            <IconButton :icon="repeatMode === 'track' ? 'repeat-one' : 'repeat'"
                class="size-l tertiary"
                :class="{
                    invert: !sameColors,
                    disabled: repeatMode === 'none',
                    solid: repeatMode !== 'none'
                }"
                @touchend.prevent.stop="repeat"
            />

            <IconButton icon="playlist"
                class="size-l tertiary"
                :class="{ invert: !sameColors }"
                @touchend.prevent.stop="openPlaylist"
            />

            <IconButton icon="chevron-down" v-if="props.collapseButton"
                class="size-l icon-xl tertiary"
                :class="{
                    invert: !sameColors
                }"
                @touchend.prevent.stop="emit('collapse')"
            />
        </div>
    </div>
</template>

<script setup>
    import { reactive, ref, computed, watch } from 'vue';
    import stores from '@/stores';
    import Slider from '🔗/components/ui/Slider.vue';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import TrackPreview from '🔗/components/ui/TrackPreview.vue';
    import TrackWave from '🔗/components/ui/TrackWave.vue';
    import Img from '🔗/components/ui/Img.vue';
    import { message } from '🔗/utils/utils';
    import AudioToggle from '🔗/components/ui/AudioToggle.vue';
    import Reaction from '🔗/components/ui/Reaction.vue';
    import api from '🔗/api';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();

    const sliderRef = ref(null);
    const modals = stores.modals();
    const player = stores.player();
    const others = stores.others();
    const locale = stores.locale();
    const messengers = stores.messengers();

    const emit = defineEmits(['collapse']);

    const props = defineProps({
        collapseButton: { type: Boolean, default: false }
    });


    const data = reactive({
        colors: {},
        tones: {}
    });

    const defaultCover = computed(() => {
        return api.tracks().defaultCover;
    });

    const color = computed(() => {
        if (!id?.value || !data.colors[id.value]) {
            return;
        }

        const color = data.colors[id.value];
        return `rgba(${color.r},${color.g},${color.b}, 1)`;
    });

    const sameColors = computed(() => {
        if (!id?.value || !data.tones[id.value]) {
            return;
        }

        return (others.theme !== 'dark' && data.tones[id.value] !== 'dark') || (others.theme === 'dark' && data.tones[id.value] === 'dark');
    });

    const state = computed(() => {
        if (!player.panes.default || player.panes.default._item?.id !== item?.value?.id) {
            return;
        }

        return player.panes.default._state;
    });

    // Repeat
        const repeatMode = computed(() => {
            return player.panes.default?._repeat;
        });

        const repeat = () => {
            const mode = repeatMode.value;

            if (mode === 'none') {
                player.repeat('queue');
            } else if (mode === 'queue') {
                player.repeat('track');
            } else {
                player.repeat('none');
            }
        };


    const prev = () => {
        if (player.panes.default) {
            player.panes.default.prev();
        }
    }

    const next = () => {
        if (player.panes.default) {
            player.panes.default.next();
        }
    }

    const id = computed(() => player.panes.default?._item?.id);

    const pos = computed(() => {
        const pane = player.panes.default;

        if (!pane || !pane._item?.id || !pane._item.duration) {
            return 0;
        }

        const pos = Math.max(0, Math.min((pane._playedSeconds * 1000), pane._item.duration));

        return Math.floor(100 * pos / pane._item.duration) + '%';
    });

    watch(
        () => id.value,
        (value) => {
            setTimeout(() => {
                if (value && sliderRef.value?.showItem) {
                    sliderRef.value.showItem(value);
                }
            });
        }
    );

    const items = computed(() => {
        if (!player.panes.default) {
            return [];
        }

        const queue = player.panes.default.queue();

        if (!queue?.items?.length) {
            if (player.panes.default._item?.id) {
                return [player.panes.default._item];
            }

            return [];
        }

        return queue?.items;
    });

    const item = computed(() => {
        if (!items.value?.length || !id.value) {
            return
        }

        return items.value.find(item => item.id === id.value);
    });

    const onChange = (payload) => {
        if (!payload?.length) {
            return;
        }

        if (!player.panes.default) {
            return [];
        }

        if (!payload[0].id || payload[0].id === id.value) {
            return;
        }

        data.id = payload[0].id;

        player.panes.default.playById(payload[0].id);
    }

    const play = async () => {
        await player.play();
    }

    const pause = async () => {
        await player.pause();
    }

    const openPlaylist = () => {
        modals.open('tracks', {
            quietClose: true
        });
    }
</script>

<style lang="scss" scoped>
    .player-detail {
        background: var(--white-500);
        height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        font: var(--font-body-2);
        position: relative;
        transition: background-color 0.4s ease;
        gap: 20px;
        padding: 6px 0;
        padding-top: calc(
            var(--max-safe-area-inset-top, var(--tg-safe-area-inset-top, 0px)) + 
            var(--max-content-safe-area-inset-top, var(--tg-content-safe-area-inset-top, 0px)) + 
            6px
        );

        .buttons {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: end;
            width: 100%;
            padding: 0 8px;
            gap: 8px;
        }

        .main {
            display: flex;
            flex-direction: column;
            gap: 20px;
            max-width: 100%;
            width: 100%;

            .preview-info {
                padding: 0 0 0 20px;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 8px;
                width: 100%;
            }

            .track-wave-box {
                width: 100%;
                padding: 0 20px;
                & > div {
                    height: 24px;
                }
            }

            .controls {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                gap: 20px;
                padding: 0 8px;
            }
        }

    }

    .slider-box {
        font: var(--font-h5);
        display: flex;
        align-items: center;
        justify-content: center;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        // border-bottom-left-radius: 8px;
        // border-bottom-right-radius: 8px;
        width: 100%;
        overflow: hidden;

        .slider {
            padding: 0px 16px;
            scroll-padding-left: 16px;
            scroll-padding-right: 16px;

            &:after,
            &:before {
                content: '';
                width: 10%;
                flex-shrink: 0;
                display: flex;
            }
        }
    }

    .slide {
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        width: 100%;
        height: 100%;
        gap: 8px;
        padding: 0;

        transition: transform 0.4s ease, opacity 0.4s ease, filter 0.4s ease;
        transform: translateZ(-300px) scale(0.8);
        opacity: 0.6;
        filter: blur(1px) brightness(0.9);
        z-index: 1;

        &.active {
            transform: translateZ(0) scale(1);
            opacity: 1;
            filter: none;
        }

        & > .progress {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            border-radius: 10px;
            background: var(--white-80);
        }

        .track-preview {
            z-index: 1;
        }

        & > button {
            flex-shrink: 0;
            z-index: 1;
        }

    }
</style>
